import React from 'react'
import PropTypes from 'prop-types'
import { compose } from "redux"
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import moment from 'moment'
import actions from './actions/root_action'
import SearchForm from './search_form'
import Listings from './listings'
import SearchMap from './map'
import { LoadingComponent } from '../../common/components/loading_component'
import Modal from '../../common/components/modal_dialog/modal_wrapper_component'
import { getInputValue, appendToRef, validate, ucfirst } from './utils'
import { countryPrefectureNames } from '../../common/constants/prefectures'
import { SORT_TYPE } from '../../common/constants/classifications'
import { getViewData } from '../../common/helpers/use_view_data'
import { withNamespaces } from "react-i18next"
import './main_container.scss'
import CurrencyHelper from '../../common/helpers/currency_helper'

const PREFECTURES = countryPrefectureNames()

const BREAKPOINTS = {
  SP: '(max-width: 767px)',
  Tablet: '(max-width: 1023px)'
}

export class Main extends React.Component{
  constructor (props) {
    super(props)

    this.onSubmitForm = this.onSubmitForm.bind(this)
    this.toggleSearchForm = this.toggleSearchForm.bind(this)
    this.state = {
      isSearchFormOpen: false,
      viewData: getViewData()
    }

    this.checkWidth = ({ media, matches }) => {
      const device = Object.keys(BREAKPOINTS).find((device) => BREAKPOINTS[device] == media)
      this.props.actions.search.setState({ [`is${device}`]: matches })
    }
    Object.keys(BREAKPOINTS).forEach((device) => {
      this.mediaQuery = this.mediaQuery || {}
      this.mediaQuery[device] = window.matchMedia(BREAKPOINTS[device])
      this.checkWidth(this.mediaQuery[device])
      this.mediaQuery[device].addListener(this.checkWidth)
    })
  }

  componentDidMount () {
    delete this._query
    delete Main.prevScrollY
    window.scrollTo(0, 0)

    if (!this.isMapMode) {
      const searchParams = {
        ...this.props.match.params,
        ...this.query,
        major_city: this.majorCitySlug,
        city: this.citySlug,
        country_code: this.countryCode,
        currency_code: CurrencyHelper.metaCurrencyCode()
      }
      const userPrefecture = this.state.viewData.userPrefecture

      if (userPrefecture) {
        searchParams.user_prefecture = userPrefecture
      }

      this.props.actions.search.getRoomGroups(searchParams)

      document.title = `${this.titleString} | ${this.titleSuffix}`
    }

    this.props.actions.propertyTypes.get()
    this.appendBreadcrumbs()
  }

  get locale() {
    const meta = document.querySelector('meta[name="language-locale"]')
    if(meta) {
      return meta.content
    }
    return null
  }

  get countryCode() {
    return (this.props.match.params.country && getInputValue('.js-country-code'))
  }

  get countryName() {
    return (this.props.match.params.country && getInputValue('.js-country-name'))
  }

  get majorCitySlug () {
    // react上のrouteではmajor_cityも `/:city/` としてmatchする
    return this.props.match.params.city && getInputValue('.js-major-city-slug-input')
  }

  get citySlug () {
    return this.props.match.params.city && getInputValue('.js-city-slug-input')
  }

  get titleSuffix() {
    return document.title.split(' | ').pop()
  }

  get titleString() {
    return Array.from(document.querySelectorAll('#search-condition-title > span')).map(el => el.innerText).join('')
  }

  componentDidUpdate () {
    delete this._query
    this.appendBreadcrumbs()

    // スクロール位置復帰
    if (this.restoreScroll) {
      delete this.restoreScroll
      if (document.body.scrollHeight > Main.prevScrollY) {
        Main.restoreScroll()
      } else {
        setTimeout(Main.restoreScroll)
      }
    }
  }

  static restoreScroll() {
    window.scrollTo(0, Main.prevScrollY >= 0 ? Main.prevScrollY : window.scrollY)
    delete Main.prevScrollY
  }

  componentWillUnmount() {
    Object.keys(this.mediaQuery).forEach((device) => this.mediaQuery[device].removeListener(this.checkWidth))
  }

  toggleSearchForm() {
    const { isSearchFormOpen } = this.state
    if (isSearchFormOpen) {
      this.restoreScroll = true
    } else {
      // スクロール位置を記憶
      Main.prevScrollY = window.scrollY
      window.scrollTo(0, 0)
    }
    this.setState({ isSearchFormOpen: !isSearchFormOpen })
  }

  static get breadcrumbList() {
    return (this._breadcrumbList = this._breadcrumbList || document.querySelector('.js-breadcrumb-list'))
  }

  appendBreadcrumbs() {
    if (this.props.match.params.prefecture && !this.isMapMode) {
      appendToRef(this.refs.breadcrumbListContainer, Main.breadcrumbList)
    }
  }

  /**
   * 既存パラメータとリンク先パラメータをマージしたパスを返す
   */
  searchPath = (query) => {
    const { arrayFormat } = this
    const params = {
      ...this.query,
      ...{ page: undefined },
      ...query
    }

    // 「目的地に近い順」になっているが、
    if (params.sort == SORT_TYPE.DISTANCE_ASC) {
      // 「目的地に近い順」にできないときは、sortパラメータを削除
      if (!params.destination && !this.props.match.params.spot_id) {
        delete params.sort
      }
    }

    // 目的地検索のときはfeature_title, room_group_id, property_idを削除
    if (params.destination && !params.keep_feature && params.feature_title) {
      delete params.feature_title
      delete params.room_group_id
      delete params.property_id
    }

    if (this.countryCode) {
      delete params.prefecture
      return `${this.props.match.url}?${queryString.stringify(params, { arrayFormat })}`
    } else if (
      !params.destination &&
      this.props.match.params.prefecture &&
      (this.props.match.params.prefecture == params.prefecture || typeof params.prefecture == "undefined")
    ) {
      delete params.prefecture
      return `${this.props.match.url}?${queryString.stringify(params, { arrayFormat })}`
    } else {
      if (params.prefecture == "") { delete params.prefecture }
      return routeUrlHelper.searchPath(params, { arrayFormat })
    }
  }

  onSubmitForm(state) {
    this.setState({ isSearchFormOpen: false })
    const method = this.isMapMode ? 'replace' : 'push'
    this.props.history[method](this.searchPath(state))
  }

  /**
   * クエリパラメータの配列形式判定
   * クエリに[]が一つでもあれば配列形式は'bracket'にする
   */
  get arrayFormat() {
    return this.props.location.search
      .split('&')
      .map(pair => decodeURIComponent(pair.split('=')[0]))
      .some(param => /\[\]/.test(param))
      ? 'bracket'
      : 'none'
  }

  get isMapMode() {
    return this.query.view == 'map'
  }

  get mapSuffixByLang() {
    switch (languageHelper.pageLocale) {
    case 'en':
      return '-en.svg'
    case 'zh-TW':
      return '-zh-TW.svg'
    default:
      return '.svg'
    }
  }

  renderChangeViewButton() {
    const { t } = this.props
    if (this.isMapMode) {
      return (
        <div styleName="back-to-list-btn">
          <Link styleName="change-view-btn" to={this.searchPath({ view: undefined })}>
            <span className="vs-icon-back-to-list" />
            <span>{ t('search.map.return') }</span>
          </Link>
        </div>
      )
    } else {
      return (
        <div styleName="map-btn">
          <Link styleName="btn-show-map" to={this.searchPath({ view: 'map' })}>
            <img src={`/assets/image/search/map/btn-show-map${this.mapSuffixByLang}`} alt="地図で探す" />
          </Link>
        </div>
      )
    }
  }

  renderContent(){
    const { isSP, isTablet } = this.props.search
    const { isSearchFormOpen } = this.state
    const isMapCls = this.isMapMode ? ' isMap' : ''
    const isSPCls = isSP ? ' isSP' : ''
    const isOpenCls = isSearchFormOpen ? ' isOpen' : ''
    const { t } = this.props

    if (isSearchFormOpen) {
      document.body.classList.contains('search-form-open') || document.body.classList.add('search-form-open')
    } else {
      document.body.classList.contains('search-form-open') && document.body.classList.remove('search-form-open')
    }

    // PITARI
    const pitariHeader = document.getElementsByClassName('pitari-header')
    if (pitariHeader.length > 0) {
      const style = this.isMapMode ? 'none' : 'block'
      pitariHeader[0].style.display = style
    }

    return(
      <React.Fragment>
        { isSP || isTablet || <div ref="breadcrumbListContainer"></div> }
        <div className={`${isMapCls}${isSPCls}${isTablet ? ' isTablet' : ''}${isOpenCls}`} styleName="container">
          <div
            styleName="toggle-btn"
            className="toggle-btn"
            onClick={this.toggleSearchForm}
          >
            { isSearchFormOpen ? t('search.sp.close') : t('search.sp.search_again') }
          </div>
          <div
            className={`column-left${this.state.isDatePickerOpen ? ' date-picker-open' : ''}`}
            styleName="column-left"
          >
            <div styleName={`sidebar ${isOpenCls}`}>
              <SearchForm
                lang={this.locale}
                isSP={isSP}
                isTablet={isTablet}
                query={this.query}
                match={this.props.match}
                countryCode={this.countryCode}
                coupons={this.props.search.available_coupons}
                onSubmitForm={this.onSubmitForm}
                place={this.place}
                t={this.props.t}
              />
            </div>
            <div style={{ marginTop: -72 }}>
              <a styleName="banner pc" href="/rakuten-stay" id="banner--search-rs20211007-pc">
                <img src="/assets/image/banner/banner-rs-all-sp.jpg" alt="" />
              </a>
              <a styleName="banner pc" href="https://experiences.travel.rakuten.co.jp/?scid=wi_oyd_search-banner" id="banner--search-rte-pc" target="_blank" rel="noreferrer noopener">
                <img src="/assets/image/banner/RTE-240x120.png" alt="" />
              </a>
            </div>
          </div>
          <div className="column-right" styleName="column-right">
            {!this.isMapMode
              ? <React.Fragment>
                { (isSP || isTablet) && <div ref="breadcrumbListContainer"></div> }
                <LoadingComponent requesting={!this.props.search.isFetched} message={ t('search.search_loading') } />
                <Listings
                  lang={this.locale}
                  search={this.props.search}
                  query={this.query}
                  match={this.props.match}
                  propertyTypes={this.props.propertyTypes.property_types}
                  onPageChange={(state) => this.props.history.push(this.searchPath(state))}
                  place={this.place}
                  searchPath={this.searchPath}
                  featureURL={this.featureURL}
                  spotId={this.spotId}
                  countryName={this.countryName}
                  coupons={this.props.search.available_coupons}
                  onSubmitForm={this.onSubmitForm}
                >
                  {this.renderChangeViewButton()}
                </Listings>
              </React.Fragment>
              : <SearchMap
                lang={languageHelper.pageLocale}
                search={this.props.search}
                actions={this.props.actions}
                query={this.query}
                match={this.props.match}
                areaName={this.areaName}
                isTablet={isTablet}
                propertyTypes={this.props.propertyTypes.property_types}
                t={t}
              >
                {this.renderChangeViewButton()}
              </SearchMap>
            }
          </div>
        </div>
        <Modal />
      </React.Fragment>
    )
  }

  render() {
    if (this.isMapMode) {
      return this.renderContent()
    }
    return (
      <div styleName="wrapper">
        {this.renderContent()}
      </div>
    )
  }

  get query() {
    const { search } = this.props.location
    if (search != this._search) {
      delete this._query
      this._search = search
    }
    return (this._query = this._query || queryString.parse(search, { arrayFormat: this.arrayFormat }))
  }

  get localizedHeadingText() {
    return getInputValue('.js-localized-heading-input')
  }

  get spotId() {
    return this.props.match.params.spot_id
  }

  get spotName() {
    return this.spotId && getInputValue('.js-spot-name-input')
  }

  get featureURL() {
    const { feature_url: url } = this.query

    const a = document.createElement('a')
    a.href = url

    if (a.host == location.host) {
      return url && url.trim()
    }
  }

  get fullAreaName() {
    return this.props.match.params.prefecture && getInputValue('.js-full-area-name-input')
  }

  get areaName() {
    return this.spotName || this.fullAreaName
  }

  get place() {
    const {
      destination,
      prefecture,
      feature_title: featureTitle,
      property_types: propertyTypes
    } = validate({
      ...this.props.match.params,
      ...this.query
    })

    const isJapanese = this.locale === 'ja'

    if (featureTitle) {
      return { key: 'featureTitle', value: featureTitle, propertyTypes }
    }

    if (destination) {
      return { key: 'destination', value: destination, propertyTypes }
    }

    if (this.areaName) {
      return {
        key: 'area',
        value: isJapanese
          ? `${this.areaName}${this.props.match.params.city || this.spotId ? '周辺' : ''}`
          : this.areaName,
        propertyTypes
      }
    }

    if (prefecture) {
      return {
        key: 'prefecture',
        value: PREFECTURES[prefecture],
        propertyTypes
      }
    }

    return {
      key: 'default',
      value: '',
      propertyTypes
    }
  }
}

const mapStateToProps = (state) => {
  return {
    login: state.login,
    language: state.language,
    search: state.search,
    propertyTypes: state.propertyTypes,
    modal: state.modal
  }
}

const mapDispatchToProps = (dispatch, state) => {
  return {
    actions: actions(dispatch)
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withNamespaces('search')
)(Main)

Main.propTypes = {
  login: PropTypes.object.isRequired,
  language: PropTypes.object.isRequired,
  search: PropTypes.object,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
}
